import type { AttrBrushModel, AttrModel, AttrPenModel, MasterDataIconModel } from "@fortex/api";
import { Field, Input, Slider } from "@fortex/map";
import type { AppLabels } from "../../i18n";
import { IconField } from "./IconUpload";
import type { IconImage } from "./IconUpload";

export const dashNames = ["dashSolid", "dashDash", "dashDot", "dashDashDot", "dashDashDotDot"] as const;

export const dashArrays: Record<number, string | undefined> = { 0: undefined, 1: "8 4", 2: "2 3", 3: "8 3 2 3", 4: "8 3 2 3 2 3" };

export function hexOf(r: number | null, g: number | null, b: number | null): string | null {
  if (r === null || g === null || b === null) return null;

  const part = (value: number) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0");

  return `#${part(r)}${part(g)}${part(b)}`;
}

export function rgbOf(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());

  if (match === null) return null;

  return { r: parseInt(match[1], 16), g: parseInt(match[2], 16), b: parseInt(match[3], 16) };
}

export function emptyPen(): AttrPenModel {
  return { width: 2, penTypeId: 0, r: 14, g: 107, b: 122, a: 255, colorRGB1: "#0e6b7a", colorRGB2: null, borderWidth: null, border_R: null, border_G: null, border_B: null, border_A: null };
}

export function emptyBrush(): AttrBrushModel {
  return { brushStyleId: 0, r: 14, g: 107, b: 122, a: 64 };
}

export function emptyIcon(): MasterDataIconModel {
  return { icon: null, iconSelected: null, iconHovered: null, originalWidth: 0, originalHeight: 0, iconGrowth: 1, width: 0, height: 0 };
}

export function penColor(pen: AttrPenModel | null): string {
  return pen?.colorRGB1 ?? hexOf(pen?.r ?? null, pen?.g ?? null, pen?.b ?? null) ?? "#0e6b7a";
}

export function brushColor(brush: AttrBrushModel | null): string {
  return hexOf(brush?.r ?? null, brush?.g ?? null, brush?.b ?? null) ?? "#0e6b7a";
}

export function brushOpacity(brush: AttrBrushModel | null): number {
  return brush?.a === null || brush?.a === undefined ? 100 : Math.round((brush.a / 255) * 100);
}

export type StyleFieldsProps = {
  attr: AttrModel | null;
  icon: MasterDataIconModel | null;
  labels: AppLabels;
  onAttrChange: (attr: AttrModel) => void;
  onIconChange: (icon: MasterDataIconModel | null) => void;
};

export function MarkerPreview({ attr, icon }: { attr: AttrModel | null; icon: MasterDataIconModel | null }) {
  const pen = attr?.attrPen ?? null;
  const brush = attr?.attrBrush ?? null;
  const stroke = penColor(pen);
  const width = pen?.width ?? 2;
  const dash = dashArrays[pen?.penTypeId ?? 0];
  const fill = brushColor(brush);
  const opacity = brushOpacity(brush) / 100;

  return (
    <span className="fxapp-marker-preview" data-fxmap="marker-preview">
      {icon?.icon ? (
        <img src={`data:image/png;base64,${icon.icon}`} alt="" />
      ) : (
        <svg viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
          <polygon points="8,36 14,12 34,10 40,30 24,42" fill={fill} fillOpacity={opacity} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
          <circle cx="24" cy="24" r="4" fill={stroke} />
        </svg>
      )}
    </span>
  );
}

export function StyleFields({ attr, icon, labels, onAttrChange, onIconChange }: StyleFieldsProps) {
  const pen = attr?.attrPen ?? null;
  const brush = attr?.attrBrush ?? null;

  const patchPen = (patch: Partial<AttrPenModel>) => onAttrChange({ ...(attr ?? { attrPen: null, attrBrush: null }), attrPen: { ...(pen ?? emptyPen()), ...patch } });
  const patchBrush = (patch: Partial<AttrBrushModel>) => onAttrChange({ ...(attr ?? { attrPen: null, attrBrush: null }), attrBrush: { ...(brush ?? emptyBrush()), ...patch } });
  const patchIcon = (key: "icon" | "iconSelected" | "iconHovered", image: IconImage | null) => {
    const base = icon ?? emptyIcon();
    const next: MasterDataIconModel = { ...base, [key]: image?.base64 ?? null };

    if (key === "icon" && image !== null) {
      next.originalWidth = image.width;
      next.originalHeight = image.height;
      next.width = image.width;
      next.height = image.height;
      next.iconGrowth = 1;
    }

    onIconChange(next.icon === null && next.iconSelected === null && next.iconHovered === null ? null : next);
  };

  return (
    <div className="fxapp-settings-section" data-fxmap="style-fields">
      <h3 className="fxapp-settings-title">{labels.settings}</h3>
      <div className="fxapp-preview">
        <MarkerPreview attr={attr} icon={icon} />
        <div className="fxapp-settings">
          <div className="fxapp-form-row">
            <Field label={labels.penColor}>
              <span className="fxapp-color">
                <input
                  type="color"
                  value={penColor(pen)}
                  onChange={event => {
                    const rgb = rgbOf(event.target.value);

                    patchPen({ colorRGB1: event.target.value, ...(rgb ?? {}) });
                  }}
                />
              </span>
            </Field>
            <Field label={labels.penWidth}>
              <Input type="number" min={0} max={20} value={String(pen?.width ?? 2)} onChange={event => patchPen({ width: Number(event.target.value) || 0 })} />
            </Field>
            <Field label={labels.penDash}>
              <select className="fxapp-select" value={String(pen?.penTypeId ?? 0)} onChange={event => patchPen({ penTypeId: Number(event.target.value) })}>
                {dashNames.map((name, index) => (
                  <option key={name} value={index}>
                    {labels[name]}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="fxapp-form-row">
            <Field label={labels.fillColor}>
              <span className="fxapp-color">
                <input
                  type="color"
                  value={brushColor(brush)}
                  onChange={event => {
                    const rgb = rgbOf(event.target.value);

                    if (rgb !== null) patchBrush(rgb);
                  }}
                />
              </span>
            </Field>
            <Field label={labels.fillOpacity}>
              <Slider value={brushOpacity(brush)} min={0} max={100} label={labels.fillOpacity} valueText={value => `${value}%`} onChange={value => patchBrush({ a: Math.round((value / 100) * 255) })} />
            </Field>
          </div>
        </div>
      </div>
      <IconField label={labels.iconNormal} value={icon?.icon ?? null} labels={labels} onChange={image => patchIcon("icon", image)} />
      <IconField label={labels.iconSelected} value={icon?.iconSelected ?? null} labels={labels} onChange={image => patchIcon("iconSelected", image)} />
      <IconField label={labels.iconHovered} value={icon?.iconHovered ?? null} labels={labels} onChange={image => patchIcon("iconHovered", image)} />
    </div>
  );
}
